import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Icon,
  MenuToggle,
  Pagination,
  SearchInput,
  Select,
  SelectList,
  SelectOption,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  KeyIcon,
  PlusCircleIcon,
  SearchIcon,
  SyncAltIcon,
  TimesCircleIcon,
} from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import { useDkimSelectors } from '../../../hooks/useDkimSelectors';
import { useMailDomainDnsZone } from '../../../hooks/useMailDomainDnsZone';
import type { DkimSelector } from '../../../types/models/DkimSelector';
import DkimCreateModal from './DkimCreateModal';
import DkimDeleteModal from './DkimDeleteModal';
import DkimDnsStatus from './DkimDnsStatus';

type DkimStatusFilter = 'all' | 'active' | 'complete' | 'incomplete';

const STATUS_LABELS: Record<DkimStatusFilter, string> = {
  all: 'All statuses',
  active: 'Active',
  complete: 'Complete',
  incomplete: 'Incomplete',
};

interface DkimTabProps {
  mailDomainId: number;
  organizationDomainId: number;
  domainName: string;
}

/**
 * Renders the per-selector DKIM health of a mail domain. Owns its own (expensive, SSH-backed) fetch
 * via {@link useDkimSelectors}, so it must be mounted lazily — the parent `<Tabs>` sets `mountOnEnter`
 * and this tab provides a Refresh action for manual re-runs. Client-side search + status filter +
 * pagination per the filterable-list-table convention (the API returns the full list in one call).
 */
function DkimTab({ mailDomainId, organizationDomainId, domainName }: DkimTabProps) {
  const { data, loading, error, refetch } = useDkimSelectors(mailDomainId);
  const selectors = useMemo(() => data?.selectors ?? [], [data]);

  // Resolve the domain's DNS-zone state once for the whole tab — each per-selector status cell offers a
  // one-click "Fix" (publish the missing/mismatched DKIM TXT record) only when the zone is managed here.
  // Fetching it here (not per row) avoids N identical zone reads. Tab is lazily mounted, so this fires
  // only once the DKIM tab is actually opened.
  const { data: dnsZone } = useMailDomainDnsZone(organizationDomainId, true);
  const managedDomainNameId = dnsZone?.zone_state === 'configured' ? dnsZone.domain_name_id : null;

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<DkimStatusFilter>('all');
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DkimSelector | null>(null);
  // Bumped on Refresh so the per-row background DNS checks re-run alongside the selector list.
  const [dnsRefreshToken, setDnsRefreshToken] = useState(0);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(handle);
  }, [search]);

  const filtered = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    return selectors.filter((s) => {
      const matchesSearch = !q || s.selector.toLowerCase().includes(q);
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && s.active) ||
        (statusFilter === 'complete' && s.complete) ||
        (statusFilter === 'incomplete' && !s.complete);
      return matchesSearch && matchesStatus;
    });
  }, [selectors, debouncedSearch, statusFilter]);

  // Clamp the requested page so a refetch that shrinks the list (e.g. a selector deleted on the host)
  // can't strand the user on an out-of-range page showing an empty table with no explanation.
  const maxPage = Math.max(1, Math.ceil(filtered.length / perPage));
  const currentPage = Math.min(page, maxPage);

  const paginated = useMemo(() => {
    const start = (currentPage - 1) * perPage;
    return filtered.slice(start, start + perPage);
  }, [filtered, currentPage, perPage]);

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('all');
    setPage(1);
  };

  // A boolean health flag, rendered as a check/cross icon paired with screen-reader text (never relying
  // on colour alone) — the muted cross deliberately avoids a danger colour, since e.g. "not active" is
  // a normal state, not an error.
  const renderFlag = (value: boolean, label: string) =>
    value ? (
      <>
        <Icon status="success">
          <CheckCircleIcon />
        </Icon>
        <span className="pf-v6-u-screen-reader">{label}: yes</span>
      </>
    ) : (
      <>
        <span style={{ color: 'var(--pf-t--global--icon--color--disabled)' }}>
          <Icon>
            <TimesCircleIcon />
          </Icon>
        </span>
        <span className="pf-v6-u-screen-reader">{label}: no</span>
      </>
    );

  const renderBody = () => {
    // Initial load only — a refetch keeps the existing table visible and surfaces progress on the
    // Refresh button instead of replacing the whole tab with a spinner.
    if (loading && !data) {
      return <CenteredSpinner />;
    }

    if (error) {
      return (
        <EmptyState
          titleText="Unable to load DKIM selectors"
          icon={ExclamationCircleIcon}
          variant="lg"
          headingLevel="h2"
          status="danger"
        >
          <EmptyStateBody>{error}</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={() => refetch()}>
                Retry
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    if (selectors.length === 0) {
      return (
        <EmptyState titleText="No DKIM keys yet" icon={KeyIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>
            This domain has no DKIM selectors on its mail server. Create one to start signing outgoing mail.
          </EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" icon={<PlusCircleIcon />} onClick={() => setIsCreateOpen(true)}>
                Create DKIM configuration
              </Button>
            </EmptyStateActions>
            <EmptyStateActions>
              <Button variant="link" icon={<SyncAltIcon />} onClick={() => refetch()} isLoading={loading}>
                Refresh
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    return (
      <>
        <Toolbar>
          <ToolbarContent>
            <ToolbarItem>
              <Button variant="primary" icon={<PlusCircleIcon />} onClick={() => setIsCreateOpen(true)}>
                Create DKIM configuration
              </Button>
            </ToolbarItem>
            <ToolbarItem>
              <SearchInput
                placeholder="Search by selector..."
                value={search}
                onChange={(_event, value) => setSearch(value)}
                onClear={() => setSearch('')}
              />
            </ToolbarItem>
            <ToolbarItem>
              <Select
                role="listbox"
                isOpen={isStatusOpen}
                selected={statusFilter}
                onSelect={(_event, value) => {
                  setStatusFilter((value as DkimStatusFilter) ?? 'all');
                  setIsStatusOpen(false);
                  setPage(1);
                }}
                onOpenChange={(open) => setIsStatusOpen(open)}
                toggle={(toggleRef) => (
                  <MenuToggle
                    ref={toggleRef}
                    onClick={() => setIsStatusOpen(!isStatusOpen)}
                    isExpanded={isStatusOpen}
                    aria-label="Filter by status"
                  >
                    {STATUS_LABELS[statusFilter]}
                  </MenuToggle>
                )}
              >
                <SelectList>
                  <SelectOption value="all">All statuses</SelectOption>
                  <SelectOption value="active">Active</SelectOption>
                  <SelectOption value="complete">Complete</SelectOption>
                  <SelectOption value="incomplete">Incomplete</SelectOption>
                </SelectList>
              </Select>
            </ToolbarItem>
            <ToolbarItem>
              <Button
                variant="secondary"
                icon={<SyncAltIcon />}
                onClick={() => {
                  void refetch();
                  setDnsRefreshToken((t) => t + 1);
                }}
                isLoading={loading}
              >
                Refresh
              </Button>
            </ToolbarItem>
            <ToolbarItem variant="pagination">
              <Pagination
                itemCount={filtered.length}
                perPage={perPage}
                page={currentPage}
                onSetPage={(_event, newPage) => setPage(newPage)}
                onPerPageSelect={(_event, newPerPage) => {
                  setPerPage(newPerPage);
                  setPage(1);
                }}
              />
            </ToolbarItem>
          </ToolbarContent>
        </Toolbar>

        {filtered.length === 0 ? (
          <EmptyState titleText="No selectors match your filters" icon={SearchIcon} variant="lg" headingLevel="h2">
            <EmptyStateBody>Try a different search term or status, or clear the filters to see all selectors.</EmptyStateBody>
            <EmptyStateFooter>
              <EmptyStateActions>
                <Button variant="primary" onClick={clearFilters}>
                  Clear filters
                </Button>
              </EmptyStateActions>
            </EmptyStateFooter>
          </EmptyState>
        ) : (
          <Table aria-label="DKIM selectors list" variant="compact">
            <Thead>
              <Tr>
                <Th style={{ verticalAlign: 'middle' }}>Selector</Th>
                <Th style={{ verticalAlign: 'middle' }}>Private key</Th>
                <Th style={{ verticalAlign: 'middle' }}>Public key</Th>
                <Th style={{ verticalAlign: 'middle' }}>KeyTable</Th>
                <Th style={{ verticalAlign: 'middle' }}>Active</Th>
                <Th style={{ verticalAlign: 'middle' }}>Complete</Th>
                <Th style={{ verticalAlign: 'middle' }}>DNS published</Th>
                <Th style={{ verticalAlign: 'middle' }} screenReaderText="Delete action" />
              </Tr>
            </Thead>
            <Tbody>
              {paginated.map((s) => (
                <Tr key={s.selector}>
                  <Td dataLabel="Selector" style={{ verticalAlign: 'middle' }}>
                    {s.selector}
                  </Td>
                  <Td dataLabel="Private key" style={{ verticalAlign: 'middle' }}>
                    {renderFlag(s.private_key_exists, 'Private key')}
                  </Td>
                  <Td dataLabel="Public key" style={{ verticalAlign: 'middle' }}>
                    {renderFlag(s.public_key_exists, 'Public key')}
                  </Td>
                  <Td dataLabel="KeyTable" style={{ verticalAlign: 'middle' }}>
                    {renderFlag(s.in_key_table, 'KeyTable entry')}
                  </Td>
                  <Td dataLabel="Active" style={{ verticalAlign: 'middle' }}>
                    {renderFlag(s.active, 'Active')}
                  </Td>
                  <Td dataLabel="Complete" style={{ verticalAlign: 'middle' }}>
                    {renderFlag(s.complete, 'Complete')}
                  </Td>
                  <Td dataLabel="DNS published" style={{ verticalAlign: 'middle' }}>
                    <DkimDnsStatus
                      mailDomainId={mailDomainId}
                      selector={s.selector}
                      domainNameId={managedDomainNameId}
                      refreshToken={dnsRefreshToken}
                    />
                  </Td>
                  <Td isActionCell style={{ verticalAlign: 'middle' }}>
                    <Button
                      variant="link"
                      isDanger
                      aria-label={`Delete selector ${s.selector}`}
                      onClick={() => setDeleteTarget(s)}
                    >
                      Delete
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}

        {filtered.length > 0 && (
          <Pagination
            itemCount={filtered.length}
            perPage={perPage}
            page={currentPage}
            onSetPage={(_event, newPage) => setPage(newPage)}
            onPerPageSelect={(_event, newPerPage) => {
              setPerPage(newPerPage);
              setPage(1);
            }}
            variant="bottom"
          />
        )}
      </>
    );
  };

  return (
    <>
      {renderBody()}
      <DkimCreateModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        mailDomainId={mailDomainId}
        organizationDomainId={organizationDomainId}
        domainName={domainName}
        onSuccess={refetch}
      />
      <DkimDeleteModal
        selector={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        mailDomainId={mailDomainId}
        organizationDomainId={organizationDomainId}
        domainName={domainName}
        onSuccess={refetch}
      />
    </>
  );
}

export default DkimTab;
